import React, { useState } from "react";
import Sidebar from "../../components/Sidebar";
import Spinner from "../../components/Spinner";
import config from "../../config";
import "./MainPage.css";

const PROVIDERS = [
  {
    id: "github",
    name: "GitHub",
    connectUrl: `${config.apiBaseUrl}${config.basePath}/auth/github/login`,
    logo: "/github-mark.svg",
  },
  {
    id: "gitea",
    name: "Gitea",
    connectUrl: `${config.apiBaseUrl}${config.basePath}/auth/gitea/login`,
    logo: "/gitea-svgrepo-com.svg",
  },
];

export default function SettingsPage() {
  // User info
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return { name: "", email: "" };
    }
  });

  // Account fields
  const [nameInput, setNameInput] = useState(user.name || "");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState<string | null>(null);

  // Plan & billing
  const [plan, setPlan] = useState<{ name: string; status: string; renews_at?: string } | null>(null);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [billingLoading, setBillingLoading] = useState(false);

  // Providers
  const [connected, setConnected] = useState<string[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(true);

  // --- Fetch plan and provider info ---
  React.useEffect(() => {
    setLoadingPlan(true);
    fetch(`${config.apiBaseUrl}${config.basePath}/user/plan`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
    })
      .then(async res => {
        if (!res.ok) throw new Error(await res.text());
        return res.json();
      })
      .then(data => setPlan(data))
      .catch(() => setPlan({ name: "Unknown", status: "Inactive" }))
      .finally(() => setLoadingPlan(false));
  }, []);

  React.useEffect(() => {
    setLoadingProviders(true);
    fetch(`${config.apiBaseUrl}${config.basePath}/user/providers`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
    })
      .then(async res => {
        if (!res.ok) throw new Error(await res.text());
        return res.json();
      })
      .then(data => setConnected(data.connected || []))
      .catch(() => setConnected([]))
      .finally(() => setLoadingProviders(false));
  }, []);

  // --- Save Name Handler ---
  const handleSaveName = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const resp = await fetch(
        `${config.apiBaseUrl}${config.basePath}/user/account`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify({ name: nameInput }),
        }
      );
      if (!resp.ok) throw new Error(await resp.text());
      setUser(u => ({ ...u, name: nameInput }));
      localStorage.setItem(
        "user",
        JSON.stringify({ ...user, name: nameInput })
      );
      setSaveMsg("Saved!");
    } catch (err: any) {
      setSaveMsg("Error saving name");
    }
    setSaving(false);
    setTimeout(() => setSaveMsg(null), 2000);
  };

  // --- Deactivate/Delete Handler ---
  const handleDeactivate = async () => {
    setDeleting(true);
    setDeleteMsg(null);
    try {
      const resp = await fetch(
        `${config.apiBaseUrl}${config.basePath}/user/deactivate`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      if (!resp.ok) throw new Error(await resp.text());
      setDeleteMsg("Account scheduled for deletion. You will be logged out.");
      setTimeout(() => {
        localStorage.removeItem("authToken");
        localStorage.removeItem("user");
        window.location.href = "/";
      }, 2200);
    } catch (err: any) {
      setDeleteMsg("Error scheduling deletion.");
    }
    setDeleting(false);
  };

  // --- Stripe portal handler ---
  const handleManageBilling = async () => {
    setBillingLoading(true);
    try {
      const res = await fetch(`${config.apiBaseUrl}${config.basePath}/billing/portal`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      if (!res.ok) throw new Error(await res.text());
      const { portal_url } = await res.json();
      window.location.href = portal_url;
    } catch (err) {
      alert("Could not open billing portal. Please contact support.");
      setBillingLoading(false);
    }
  };

  return (
    <div className="dashboard-root">
      <Sidebar user={user} current="settings" />
      <div className="main-content">
        <div className="overview-grid">

          {/* Account Card */}
          <div className="card">
            <div className="card-title">Account</div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontWeight: 500, marginBottom: 2 }}>Name</label>
              <input
                style={{
                  fontSize: "1em",
                  padding: "8px 12px",
                  borderRadius: 7,
                  border: "1px solid #dae1ed",
                  marginBottom: 5,
                  width: "95%",
                  maxWidth: 370,
                }}
                value={nameInput}
                disabled={saving}
                onChange={e => setNameInput(e.target.value)}
                maxLength={48}
              />
              <button
                onClick={handleSaveName}
                disabled={saving || nameInput === user.name}
                style={{
                  background: "#2563eb",
                  color: "#fff",
                  border: "none",
                  borderRadius: 5,
                  padding: "7px 18px",
                  fontWeight: 500,
                  fontSize: 15,
                  marginLeft: 8,
                  cursor: saving ? "wait" : "pointer",
                  opacity: nameInput === user.name ? 0.5 : 1,
                }}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              {saveMsg && (
                <span style={{ marginLeft: 12, color: saveMsg === "Saved!" ? "#16a34a" : "#ef4444" }}>
                  {saveMsg}
                </span>
              )}
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontWeight: 500, marginBottom: 2 }}>Email</label>
              <input
                style={{
                  fontSize: "1em",
                  padding: "8px 12px",
                  borderRadius: 7,
                  border: "1px solid #dae1ed",
                  width: "95%",
                  maxWidth: 370,
                  background: "#f3f6fa",
                  color: "#888",
                }}
                value={user.email || ""}
                readOnly
              />
            </div>
            <button
              style={{
                background: "#fff",
                color: "#ef4444",
                border: "1.5px solid #ef4444",
                borderRadius: 6,
                padding: "8px 22px",
                fontWeight: 500,
                fontSize: 15,
                marginTop: 10,
                cursor: "pointer",
                boxShadow: "0 1px 2px #0001",
                transition: "background 0.15s, color 0.15s",
              }}
              onClick={() => setShowDeleteModal(true)}
            >
              Deactivate Account
            </button>
            {showDeleteModal && (
              <div
                style={{
                  position: "fixed",
                  top: 0, left: 0, width: "100vw", height: "100vh",
                  background: "#1e293b88",
                  zIndex: 20,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                }}>
                <div style={{
                  background: "#fff",
                  padding: "40px 32px 32px 32px",
                  borderRadius: 13,
                  maxWidth: 400,
                  boxShadow: "0 6px 36px #0003",
                  textAlign: "center"
                }}>
                  <div style={{ fontWeight: 700, fontSize: 20, color: "#1e293b", marginBottom: 12 }}>
                    Confirm Deactivation
                  </div>
                  <div style={{ color: "#b91c1c", marginBottom: 18, fontSize: 16 }}>
                    Are you sure you want to schedule to deactivate your account?<br />
                    Your account will remain active until the end of the current billing period.<br />
                  </div>
                  {deleteMsg && (
                    <div style={{ marginBottom: 8, color: "#16a34a" }}>
                      {deleteMsg}
                    </div>
                  )}
                  <button
                    onClick={handleDeactivate}
                    disabled={deleting}
                    style={{
                      background: "#ef4444",
                      color: "#fff",
                      border: "none",
                      borderRadius: 5,
                      padding: "8px 26px",
                      fontWeight: 600,
                      fontSize: 16,
                      cursor: deleting ? "wait" : "pointer",
                      marginRight: 16,
                    }}>
                    {deleting ? "Deactivating..." : "Deactivate"}
                  </button>
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    style={{
                      background: "#f3f6fa",
                      color: "#25416a",
                      border: "none",
                      borderRadius: 5,
                      padding: "8px 18px",
                      fontWeight: 500,
                      fontSize: 15,
                      cursor: "pointer",
                    }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Plan & Billing Card */}
          <div className="card">
            <div className="card-title">Plan & Billing</div>
            {loadingPlan ? (
              <Spinner centered />
            ) : (
              <div style={{ padding: 10, minHeight: 110 }}>
                <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 2 }}>
                  Current Plan: {plan?.name ?? "Unknown"}
                </div>
                <div
                  style={{
                    fontSize: 15,
                    color: plan?.status === "active" ? "#16a34a" : "#c026d3",
                    marginBottom: 8,
                  }}
                >
                  Status: {plan?.status ?? "Unknown"}
                </div>
                {plan?.renews_at && (
                  <div style={{ fontSize: 13, color: "#888", marginBottom: 12 }}>
                    Renews: {new Date(plan.renews_at).toLocaleDateString()}
                  </div>
                )}
                <button
                  onClick={handleManageBilling}
                  disabled={billingLoading}
                  style={{
                    background: "#7c3aed",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "9px 26px",
                    fontWeight: 500,
                    fontSize: 16,
                    cursor: "pointer",
                    boxShadow: "0 1px 2px #0001",
                  }}
                >
                  {billingLoading ? "Redirecting..." : "Manage Billing"}
                </button>
              </div>
            )}
          </div>

          {/* Connected Providers Card */}
          <div className="card">
            <div className="card-title">Connected Providers</div>
            {loadingProviders ? (
              <Spinner centered label="Loading provider status..." />
            ) : (
              <div
                style={{
                  display: "flex",
                  gap: 32,
                  flexWrap: "wrap",
                  marginTop: 8,
                }}
              >
                {PROVIDERS.map(provider => {
                  const isConnected = connected.includes(provider.id);
                  return (
                    <div
                      key={provider.id}
                      style={{
                        background: isConnected ? "#e0f7fa" : "#fff",
                        border: isConnected ? "1.5px solid #38bdf8" : "1.5px solid #ddd",
                        borderRadius: 8,
                        boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
                        padding: 20,
                        minWidth: 200,
                        maxWidth: 290,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={provider.logo}
                        alt={`${provider.name} logo`}
                        style={{
                          width: 38,
                          height: 38,
                          marginBottom: 8,
                          filter: isConnected ? "grayscale(0%)" : "grayscale(60%)",
                          opacity: isConnected ? 1 : 0.7,
                        }}
                      />
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 18,
                          marginBottom: 4,
                          color: isConnected ? "#0e7490" : "#222",
                        }}
                      >
                        {provider.name}
                      </div>
                      <div
                        style={{
                          fontSize: 15,
                          color: isConnected ? "#16a34a" : "#aaa",
                          marginBottom: 12,
                        }}
                      >
                        {isConnected ? "Connected" : "Not Connected"}
                      </div>
                      {isConnected ? (
                        <button
                          style={{
                            background: "#fff",
                            color: "#ef4444",
                            border: "1px solid #ef4444",
                            borderRadius: 5,
                            padding: "7px 18px",
                            fontWeight: 500,
                            cursor: "pointer",
                          }}
                          onClick={() => alert("Disconnect not implemented yet.")}
                        >
                          Disconnect
                        </button>
                      ) : (
                        <a
                          href={provider.connectUrl}
                          style={{
                            background: "#2563eb",
                            color: "#fff",
                            border: "none",
                            borderRadius: 5,
                            padding: "8px 18px",
                            fontWeight: 500,
                            cursor: "pointer",
                            textDecoration: "none",
                            boxShadow: "0 1px 2px #0001",
                          }}
                        >
                          Connect {provider.name}
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
